// src/pages/Workflows/Editor/components/NodeConfigPanel.js
// Coluna direita do Editor: configuração do nó selecionado

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { NODE_DEFINITIONS } from '../utils/nodeDefinitions';

export default function NodeConfigPanel({ node, allNodes, onUpdateConfig, onDeleteNode }) {
  const [modelos, setModelos] = useState([]);

  // Carregar modelos da API (para seletor de modelo no prompt_ia)
  useEffect(() => {
    let mounted = true;
    axios.get('/api/workflows/modelos')
      .then(r => { if (mounted && r.data.success) setModelos(r.data.data); })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  if (!node) {
    return (
      <div className="wf-config-panel wf-config-empty">
        <p>Selecione um nó para configurar</p>
      </div>
    );
  }

  const { nodeType, config = {} } = node.data;
  const def = NODE_DEFINITIONS[nodeType];
  if (!def) return null;

  const configInputs = config.inputs || {};
  const configParams = config.params || {};
  const configCampos = config.campos || [];

  const updateInput = (campo, valor) => {
    onUpdateConfig(node.id, {
      inputs: { ...configInputs, [campo]: valor },
    });
  };

  const updateParam = (param, valor) => {
    onUpdateConfig(node.id, {
      params: { ...configParams, [param]: valor },
    });
  };

  // Nós disponíveis como fonte (todos excepto o próprio)
  const nosDisponiveis = allNodes.filter(n => n.id !== node.id);

  return (
    <div className="wf-config-panel">
      <div className="wf-config-header">
        <span className="wf-config-title">{def.label}</span>
        <button
          className="wf-btn wf-btn-icon wf-btn-danger"
          title="Remover nó"
          onClick={() => onDeleteNode(node.id)}
        >
          🗑
        </button>
      </div>

      <div className="wf-config-nodeid">ID: {node.id}</div>

      {/* ── Campos de Input ─────────────────────────────────────── */}
      {def.inputs && def.inputs.length > 0 && (
        <div className="wf-config-section">
          <div className="wf-config-section-title">Inputs</div>
          {def.inputs.map(inp => {
            const inputConf = configInputs[inp.nome] || { fonte: 'fixo', valor: '' };

            return (
              <div key={inp.nome} className="wf-config-field">
                <label className="wf-config-label">
                  {inp.label || inp.nome}
                  {inp.obrigatorio && <span style={{ color: '#ef4444' }}> *</span>}
                </label>

                {/* Seletor de fonte */}
                <select
                  className="wf-config-select"
                  value={inputConf.fonte || 'fixo'}
                  onChange={e => updateInput(inp.nome, { ...inputConf, fonte: e.target.value })}
                >
                  <option value="fixo">Valor fixo</option>
                  <option value="node_output">Saída de nó</option>
                </select>

                {/* Valor fixo */}
                {inputConf.fonte === 'fixo' && (
                  <textarea
                    className="wf-config-textarea"
                    rows={3}
                    placeholder={`Valor para "${inp.label || inp.nome}"...`}
                    value={inputConf.valor || ''}
                    onChange={e => updateInput(inp.nome, { ...inputConf, valor: e.target.value })}
                  />
                )}

                {/* Saída de nó anterior */}
                {inputConf.fonte === 'node_output' && (
                  <select
                    className="wf-config-select"
                    value={inputConf.no_id || ''}
                    onChange={e => updateInput(inp.nome, { ...inputConf, no_id: e.target.value })}
                  >
                    <option value="">-- Escolher nó --</option>
                    {nosDisponiveis.map(n => {
                      const d = NODE_DEFINITIONS[n.data?.nodeType];
                      return (
                        <option key={n.id} value={n.id}>
                          {d?.label || n.data?.nodeType} ({n.id.substring(0, 8)}...)
                        </option>
                      );
                    })}
                  </select>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Parâmetros Estáticos ─────────────────────────────────── */}
      {def.params_estaticos && def.params_estaticos.length > 0 && (
        <div className="wf-config-section">
          <div className="wf-config-section-title">Parâmetros</div>
          {def.params_estaticos.map(param => (
            <div key={param.nome} className="wf-config-field">
              <label className="wf-config-label">{param.label || param.nome}</label>

              {param.tipo === 'modelo_selector' && (
                <select
                  className="wf-config-select"
                  value={configParams[param.nome] || ''}
                  onChange={e => updateParam(param.nome, e.target.value)}
                >
                  <option value="">-- Selecionar modelo --</option>
                  {modelos.map(m => (
                    <option key={m.id} value={m.id}>{m.nome || m.id}</option>
                  ))}
                </select>
              )}

              {param.tipo === 'lista' && (
                <select
                  className="wf-config-select"
                  value={configParams[param.nome] ?? param.default ?? ''}
                  onChange={e => updateParam(param.nome, e.target.value)}
                >
                  {/* opcoes aceita string ('high') ou objecto ({ valor, label }) */}
                  {(param.opcoes || []).map(op => {
                    const valor = typeof op === 'object' ? op.valor : op;
                    const label = typeof op === 'object' ? op.label : op;
                    return <option key={valor} value={valor}>{label}</option>;
                  })}
                </select>
              )}

              {param.tipo === 'booleano' && (
                <label className="wf-config-checkbox-label">
                  <input
                    type="checkbox"
                    checked={configParams[param.nome] ?? param.default ?? false}
                    onChange={e => updateParam(param.nome, e.target.checked)}
                  />
                  <span>{param.label || param.nome}</span>
                </label>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Campos do Formulário (nó formulario) ─────────────────── */}
      {nodeType === 'formulario' && (
        <CamposFormulario campos={configCampos} onChange={campos => onUpdateConfig(node.id, { campos })} />
      )}

      {/* ── Entradas do Unificar Valores ─────────────────────────── */}
      {nodeType === 'unificar_valores' && (
        <EntradasUnificar
          entradas={configParams.entradas || []}
          configInputs={configInputs}
          nosDisponiveis={nosDisponiveis}
          onChangeEntradas={entradas => onUpdateConfig(node.id, { params: { ...configParams, entradas } })}
          onChangeInput={(input_nome, valor) => onUpdateConfig(node.id, { inputs: { ...configInputs, [input_nome]: valor } })}
          onRemoverEntrada={(idx, input_nome) => {
            // Atualização atómica: remove de inputs e de params.entradas em simultâneo
            const { [input_nome]: _removido, ...restInputs } = configInputs;
            const novasEntradas = (configParams.entradas || []).filter((_, i) => i !== idx);
            onUpdateConfig(node.id, { inputs: restInputs, params: { ...configParams, entradas: novasEntradas } });
          }}
        />
      )}

      {/* Output type (read-only) */}
      <div className="wf-config-output">
        <span>Output:</span>
        <span className="wf-config-output-badge">{def.outputType}</span>
      </div>
    </div>
  );
}

// Sub-componente para configurar entradas dinâmicas do nó Unificar Valores
function EntradasUnificar({ entradas, configInputs, nosDisponiveis, onChangeEntradas, onChangeInput, onRemoverEntrada }) {
  const adicionarEntrada = () => {
    const input_nome = `entrada_${Date.now()}`;
    onChangeEntradas([...entradas, { nome: '', input_nome }]);
  };

  const removerEntrada = (idx) => {
    onRemoverEntrada(idx, entradas[idx].input_nome);
  };

  const atualizarNome = (idx, nome) => {
    onChangeEntradas(entradas.map((e, i) => i === idx ? { ...e, nome } : e));
  };

  return (
    <div className="wf-config-section">
      <div className="wf-config-section-title" style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span>Entradas</span>
        <button
          className="wf-btn wf-btn-secondary"
          style={{ fontSize: '0.75rem', padding: '3px 8px' }}
          onClick={adicionarEntrada}
        >
          + Entrada
        </button>
      </div>

      {entradas.length === 0 && (
        <p style={{ fontSize: '0.8rem', color: '#94a3b8', margin: '8px 0' }}>
          Sem entradas definidas
        </p>
      )}

      {entradas.map((entrada, idx) => {
        const inputConf = configInputs[entrada.input_nome] || { fonte: 'node_output', no_id: '' };
        return (
          <div key={entrada.input_nome} style={{ marginBottom: 10, borderLeft: '2px solid #f97316', paddingLeft: 8 }}>
            <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginBottom: 4 }}>
              <input
                className="wf-config-input"
                placeholder="Nome (ex: Análise do Caso)"
                value={entrada.nome}
                onChange={e => atualizarNome(idx, e.target.value)}
                style={{ flex: 1 }}
              />
              <button
                className="wf-btn wf-btn-icon wf-btn-danger"
                style={{ padding: '4px 8px' }}
                onClick={() => removerEntrada(idx)}
              >
                ✕
              </button>
            </div>
            <select
              className="wf-config-select"
              value={inputConf.no_id || ''}
              onChange={e => onChangeInput(entrada.input_nome, { fonte: 'node_output', no_id: e.target.value })}
            >
              <option value="">-- Origem: escolher nó --</option>
              {nosDisponiveis.map(n => {
                const d = NODE_DEFINITIONS[n.data?.nodeType];
                return (
                  <option key={n.id} value={n.id}>
                    {d?.label || n.data?.nodeType} ({n.id.substring(0, 8)}...)
                  </option>
                );
              })}
            </select>
          </div>
        );
      })}
    </div>
  );
}

// Sub-componente para configurar campos do nó Formulário
function CamposFormulario({ campos = [], onChange }) {
  const adicionarCampo = () => {
    onChange([...campos, { nome: '', tipo: 'text', label: '', obrigatorio: false }]);
  };

  const removerCampo = (idx) => {
    onChange(campos.filter((_, i) => i !== idx));
  };

  const atualizarCampo = (idx, campo, valor) => {
    const novos = campos.map((c, i) => i === idx ? { ...c, [campo]: valor } : c);
    onChange(novos);
  };

  return (
    <div className="wf-config-section">
      <div className="wf-config-section-title" style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span>Campos do Formulário</span>
        <button className="wf-btn wf-btn-secondary" style={{ fontSize: '0.75rem', padding: '3px 8px' }} onClick={adicionarCampo}>
          + Campo
        </button>
      </div>

      {campos.length === 0 && (
        <p style={{ fontSize: '0.8rem', color: '#94a3b8', margin: '8px 0' }}>
          Sem campos definidos
        </p>
      )}

      {campos.map((campo, idx) => (
        <div key={idx} className="wf-config-campo-row">
          <input
            className="wf-config-input"
            placeholder="Nome (ex: cliente_nome)"
            value={campo.nome}
            onChange={e => atualizarCampo(idx, 'nome', e.target.value)}
          />
          <input
            className="wf-config-input"
            placeholder="Label (ex: Nome do Cliente)"
            value={campo.label}
            onChange={e => atualizarCampo(idx, 'label', e.target.value)}
          />
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <select
              className="wf-config-select"
              value={campo.tipo}
              onChange={e => atualizarCampo(idx, 'tipo', e.target.value)}
              style={{ flex: 1 }}
            >
              <option value="text">Texto</option>
              <option value="textarea">Texto longo</option>
              <option value="number">Número</option>
              <option value="date">Data</option>
              <option value="caso_analisado">Caso Analisado</option>
            </select>
            <label style={{ fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
              <input
                type="checkbox"
                checked={campo.obrigatorio}
                onChange={e => atualizarCampo(idx, 'obrigatorio', e.target.checked)}
              />
              Obrig.
            </label>
            <button
              className="wf-btn wf-btn-icon wf-btn-danger"
              style={{ padding: '4px 8px' }}
              onClick={() => removerCampo(idx)}
            >
              ✕
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
